import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User,
  PencilRuler,
  HardHat,
  Building,
  Download,
  Edit,
  Save,
  Plus,
  Trash2,
  Lock
} from 'lucide-react';
import { collection, getDocs, updateDoc, deleteDoc, doc, query, where, serverTimestamp, addDoc, getDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, getDownloadURL, getBytes } from 'firebase/storage';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getAccountById } from '../services/firestoreService';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import StartSidebar from '../components/StartSidebar';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const StartJobDescriptions = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('design');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [jobs, setJobs] = useState({
    design: [],
    construction: [],
    office: []
  });
  const [userCustomizations, setUserCustomizations] = useState({});
  const [unlockedJobs, setUnlockedJobs] = useState(null); // null means not set, array means explicitly set
  const modalRef = useRef(null);

  const tabs = [
    { id: 'design', label: 'Design', icon: PencilRuler },
    { id: 'construction', label: 'Construction', icon: HardHat },
    { id: 'office', label: 'Office', icon: Building }
  ];

  // Fetch job descriptions from Firestore and user customizations
  useEffect(() => {
    if (currentUser) {
      fetchJobs();
      fetchUserCustomizations();
    }
  }, [currentUser]);

  const fetchJobs = async () => {
    try {
      // Get user's accountId from their user document
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      let accountId = null;
      let unlockedJobsList = null; // null means not set, array means explicitly set
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        accountId = userData.accountId;
        
        // Fetch account data to get unlockedJobs
        if (accountId) {
          try {
            const accountData = await getAccountById(accountId);
            if (accountData && accountData.unlockedJobs !== undefined) {
              unlockedJobsList = Array.isArray(accountData.unlockedJobs) ? accountData.unlockedJobs : [];
            }
          } catch (error) {
            console.error('Error fetching account data:', error);
            // Continue without unlockedJobs filter if account fetch fails
          }
        }
      }

      // Store unlockedJobs in state for checking during render
      setUnlockedJobs(unlockedJobsList);

      // Fetch all published job descriptions from templates
      const templateQuerySnapshot = await getDocs(collection(db, 'jobDescriptions'));
      const templateJobs = templateQuerySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(job => job.status === 'published'); // Only show published jobs

      // Fetch account-specific jobs if accountId exists
      let accountJobs = [];
      if (accountId) {
        try {
          // Account-specific jobs might be in userJobCustomizations with isCombined flag
          const accountJobsQuery = query(
            collection(db, 'userJobCustomizations'),
            where('userId', '==', accountId),
            where('isCombined', '==', true)
          );
          const accountJobsSnapshot = await getDocs(accountJobsQuery);
          accountJobs = accountJobsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error('Error fetching account-specific jobs:', error);
          // Continue with just template jobs if account jobs fail to load
        }
      }

      // Combine template jobs and account-specific jobs - show ALL jobs
      const allJobs = [...templateJobs, ...accountJobs];
      
      // Group jobs by department
      const groupedJobs = {
        design: allJobs.filter(job => job.department === 'design'),
        construction: allJobs.filter(job => job.department === 'construction'),
        office: allJobs.filter(job => job.department === 'office')
      };
      
      setJobs(groupedJobs);
    } catch (error) {
      console.error('Error fetching job descriptions:', error);
    }
  };

  const fetchUserCustomizations = async () => {
    try {
      // Get all user customizations (non-combined jobs)
      const customizationsQuery = query(
        collection(db, 'userJobCustomizations'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(customizationsQuery);
      const customizations = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Only include non-combined jobs (template customizations)
        if (!data.isCombined && data.templateId) {
          customizations[data.templateId] = {
            id: doc.id,
            ...data
          };
        }
      });
      
      setUserCustomizations(customizations);
    } catch (error) {
      console.error('Error fetching user customizations:', error);
    }
  };

  const handleJobClick = useCallback((job, event) => {
    // Prevent event propagation to avoid interference from other handlers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate job data
    if (!job || !job.id) {
      console.error('Invalid job data:', job);
      alert('Error: Invalid job data. Please try again.');
      return;
    }
    
    try {
      // Check if user has customizations
      const userCustomization = userCustomizations[job.id];
      
      // Start with the template job data and ensure all required fields exist
      let displayJob = {
        id: job.id,
        title: job.title || 'Untitled Job',
        department: job.department || activeTab,
        level: job.level || '',
        details: job.details || {
          reportsTo: '',
          purpose: '',
          dutiesAndResponsibilities: [],
          requirements: [],
          tools: [],
          physical: [],
          directReports: []
        },
        ...job
      };
      
      // If user has customizations, merge them with the template
      if (userCustomization) {
        displayJob = {
          ...displayJob,
          title: userCustomization.title || job.title || 'Untitled Job',
          level: userCustomization.level || job.level || '',
          details: {
            ...displayJob.details,
            ...(userCustomization.details || {})
          }
        };
      }
      
      // Store the original template ID for saving customizations
      displayJob.templateId = job.id;
      displayJob.hasCustomizations = !!userCustomization;
      
      setSelectedJob(displayJob);
      setEditedJob(displayJob);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error handling job click:', error);
      alert('Error opening job description. Please try again.');
    }
  }, [userCustomizations, activeTab]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const customizationData = {
        userId: currentUser.uid,
        templateId: selectedJob.templateId,
        title: editedJob.title || '',
        level: editedJob.level || '',
        details: editedJob.details || {},
        department: editedJob.department || '',
        updatedAt: serverTimestamp()
      };

      if (selectedJob.hasCustomizations) {
        // Update existing customization
        const customizationId = userCustomizations[selectedJob.templateId].id;
        const docRef = doc(db, 'userJobCustomizations', customizationId);
        await updateDoc(docRef, customizationData);
      } else {
        // Create new customization
        const docRef = await addDoc(collection(db, 'userJobCustomizations'), {
          ...customizationData,
          createdAt: serverTimestamp()
        });
        
        // Update local customizations state
        setUserCustomizations(prev => ({
          ...prev,
          [selectedJob.templateId]: {
            id: docRef.id,
            ...customizationData
          }
        }));
      }
      
      // Update local state to show the customized version
      setSelectedJob({
        ...editedJob,
        hasCustomizations: true
      });
      
      // Update the jobs display to show customized version
      setJobs(prev => ({
        ...prev,
        [editedJob.department]: prev[editedJob.department].map(job => 
          job.id === selectedJob.templateId ? {
            ...job,
            title: editedJob.title,
            level: editedJob.level,
            details: editedJob.details
          } : job
        )
      }));
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving job customization:', error);
      alert('Error saving job customization. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedJob({ ...selectedJob });
    setIsEditing(false);
  };

  const handleResetToTemplate = async () => {
    try {
      if (selectedJob.hasCustomizations) {
        // Delete the user customization
        const customizationId = userCustomizations[selectedJob.templateId].id;
        await deleteDoc(doc(db, 'userJobCustomizations', customizationId));
        
        // Remove from local state
        setUserCustomizations(prev => {
          const newCustomizations = { ...prev };
          delete newCustomizations[selectedJob.templateId];
          return newCustomizations;
        });
        
        // Reset the job to template version
        const templateJob = jobs[selectedJob.department].find(job => job.id === selectedJob.templateId);
        const resetJob = { ...templateJob, templateId: templateJob.id, hasCustomizations: false };
        
        setSelectedJob(resetJob);
        setEditedJob(resetJob);
        
        // Update jobs display to show template version
        setJobs(prev => ({
          ...prev,
          [selectedJob.department]: prev[selectedJob.department].map(job => 
            job.id === selectedJob.templateId ? {
              ...templateJob,
              title: templateJob.title,
              level: templateJob.level,
              details: templateJob.details
            } : job
          )
        }));
      }
    } catch (error) {
      console.error('Error resetting to template:', error);
    }
  };

  const renderJobDetails = (job) => {
    const details = job.details || {};
    
    return (
      <div className="space-y-4">
        {details.reportsTo && (
          <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Reports To</h4>
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{details.reportsTo}</p>
          </div>
        )}
        
        {details.purpose && (
          <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Purpose</h4>
            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{details.purpose}</p>
          </div>
        )}
        
        {details.dutiesAndResponsibilities && details.dutiesAndResponsibilities.length > 0 && (
          <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Duties and Responsibilities</h4>
            <ul className="list-disc list-inside space-y-2">
              {details.dutiesAndResponsibilities.map((item, index) => (
                <li key={index} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <span className="font-medium">{item.duty || item.title}:</span> {item.responsibility || item.description}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {details.requirements && details.requirements.length > 0 && (
          <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Requirements</h4>
            <ul className="list-disc list-inside space-y-2">
              {details.requirements.map((item, index) => (
                <li key={index} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <span className="font-medium">{item.topic || item.title}:</span> {item.requisite || item.description}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {details.tools && details.tools.length > 0 && details.tools[0] && (
          <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Tools</h4>
            <ul className="list-disc list-inside space-y-1">
              {details.tools.map((tool, index) => tool && (
                <li key={index} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{tool}</li>
              ))}
            </ul>
          </div>
        )}
        
        {details.physical && details.physical.length > 0 && details.physical[0] && (
          <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Physical Requirements</h4>
            <ul className="list-disc list-inside space-y-1">
              {details.physical.map((req, index) => req && (
                <li key={index} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{req}</li>
              ))}
            </ul>
          </div>
        )}
        
        {details.directReports && details.directReports.length > 0 && details.directReports[0] && (
          <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 border`}>
            <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Direct Reports</h4>
            <ul className="list-disc list-inside space-y-1">
              {details.directReports.map((report, index) => report && (
                <li key={index} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{report}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className={`min-h-screen p-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="rounded-lg p-6">
            <div className="flex items-center justify-between mb-8">
              <BackButton onClick={() => navigate('/start/operations')} />
              <div className="flex items-center">
                <User className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Job Descriptions</h1>
              </div>
              <div className="w-[120px] flex justify-end">
                <AddButton onClick={() => {}} disabled />
              </div>
            </div>

            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
            {/* Tabs */}
            <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-8`}>
              <nav className="-mb-px flex justify-center space-x-8" aria-label="Tabs">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                        ${activeTab === tab.id
                          ? 'border-red-500 text-red-600'
                          : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                        }
                      `}
                    >
                      <div className="flex items-center">
                        <Icon className="w-4 h-4 mr-2" />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Job Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {jobs[activeTab] && jobs[activeTab].length > 0 ? (
                jobs[activeTab].map((job, index) => {
                  // Get the display title - use customized title if available, otherwise template title
                  const displayTitle = userCustomizations[job.id]?.title || job.title || 'Untitled Job';
                  const hasCustomizations = !!userCustomizations[job.id];
                  
                  // Check if this job is unlocked
                  // If unlockedJobs is null (not set), all jobs are unlocked (backward compatibility)
                  // If unlockedJobs is an array, only jobs in that array are unlocked
                  const isUnlocked = unlockedJobs === null || (Array.isArray(unlockedJobs) && unlockedJobs.includes(job.id));
                  
                  return (
                    <div
                      key={job.id || index}
                      className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} border rounded-lg p-6 transition-shadow duration-200 relative ${
                        isUnlocked 
                          ? 'hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer' 
                          : 'opacity-60 cursor-not-allowed'
                      }`}
                      onClick={(event) => {
                        if (!isUnlocked) {
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                        // Add a small delay to ensure the click is processed
                        setTimeout(() => handleJobClick(job, event), 0);
                      }}
                      onMouseDown={(event) => {
                        if (!isUnlocked) {
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                        // Prevent default to avoid any interference
                        event.preventDefault();
                      }}
                    >
                      {/* Lock icon for locked jobs */}
                      {!isUnlocked && (
                        <div className="absolute top-3 right-3">
                          <Lock className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                      )}
                    <div className="flex flex-col items-center mb-4">
                      <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>{displayTitle}</h3>
                      {job.level && (
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{job.level}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {hasCustomizations ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                          }`}>
                            Tailored
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                          }`}>
                            Template
                          </span>
                        )}
                      </div>
                    </div>
                    <button 
                      className={`w-full py-1.5 px-3 rounded-md transition-colors duration-200 text-sm ${
                        isUnlocked
                          ? darkMode 
                            ? 'bg-red-600 text-white hover:bg-red-700' 
                            : 'bg-red-600 text-white hover:bg-red-700'
                          : darkMode
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      disabled={!isUnlocked}
                    >
                      {isUnlocked ? 'View Details' : 'Locked'}
                    </button>
                  </div>
                  );
                })
              ) : (
                <div className={`col-span-full text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <p className="text-lg">No job descriptions found for this department.</p>
                  <p className="text-sm mt-2">Create job descriptions in the Templates section first.</p>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div ref={modalRef} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto`}>
            <div className="p-4 sm:p-6 md:p-8">
              {(() => {
                try {
                  return (
                    <>
                      <div className="flex flex-col items-center mb-6 sm:mb-8">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedJob.title}
                            onChange={(e) => setEditedJob({ ...editedJob, title: e.target.value })}
                            className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center border rounded px-2 py-1 w-full ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        ) : (
                          <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center px-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedJob.title}</h2>
                        )}
                        {selectedJob.level && (
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedJob.level}</p>
                        )}
                      </div>

                      <div className="space-y-4 sm:space-y-6">
                        {renderJobDetails(isEditing ? editedJob : selectedJob)}
                      </div>

                      {/* Action Buttons */}
                      <div className={`mt-6 sm:mt-8 pt-4 sm:pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0`}>
                        <div className="flex flex-wrap gap-2 sm:space-x-4">
                          {selectedJob.hasCustomizations && !isNewTemplate && (
                            <button
                              onClick={handleResetToTemplate}
                              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              Reset to Template
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 sm:space-x-4 justify-end">
                          {!isNewTemplate && (
                            <button
                              onClick={() => setIsModalOpen(false)}
                              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                            >
                              Close
                            </button>
                          )}
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleCancel}
                                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                              >
                                Cancel
                              </button>
                              <SaveButton onClick={handleSave} />
                            </>
                          ) : (
                            <EditButton onClick={handleEdit} />
                          )}
                        </div>
                      </div>
                    </>
                  );
                } catch (error) {
                  console.error('Error rendering job modal:', error);
                  return (
                    <div className="text-center py-8">
                      <h2 className="text-xl font-semibold text-red-600 mb-4">Error Loading Job Description</h2>
                      <p className="text-gray-600 mb-4">There was an error loading this job description. Please try again.</p>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Close
                      </button>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartJobDescriptions;

